import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Check, X, Plus } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Modal } from '@/shared/components/ui/Modal';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useRegularisations, useRegularisationActions } from '../hooks/useBiometricAdmin';
import { istDateTime } from '../biometricTypes';

const TONE: Record<string, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
};

/** Missed-punch corrections: employees raise them, managers decide. */
export function RegularisationPanel() {
  const [filter, setFilter] = useState('pending');
  const { data: items, isLoading } = useRegularisations(filter || undefined);
  const { raise, decide } = useRegularisationActions();

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ log_date: '', requested_in: '', requested_out: '', reason: '' });

  const submit = async () => {
    if (!form.log_date || !form.reason.trim()) {
      toast.error('The date and a reason are both required.');
      return;
    }
    try {
      await raise.mutateAsync(form);
      toast.success('Request submitted for approval.');
      setCreating(false);
      setForm({ log_date: '', requested_in: '', requested_out: '', reason: '' });
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const decideOn = async (id: string, approve: boolean) => {
    const remarks = window.prompt(approve ? 'Approval remarks (optional)' : 'Reason for rejection') ?? '';
    if (!approve && !remarks.trim()) {
      toast.error('Give a reason when rejecting.');
      return;
    }
    try {
      await decide.mutateAsync({ id, approve, remarks });
      toast.success(approve ? 'Approved — the day was updated.' : 'Rejected.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <Card className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">Regularisations</h2>
          <p className="text-sm text-on-surface-variant">
            Corrections for days where someone forgot to punch. Approving one overrides that day&apos;s
            attendance and records who approved it.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            className="h-9 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Raise request
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : !items?.length ? (
        <p className="py-8 text-center text-sm text-on-surface-variant">
          Nothing {filter || 'to show'}.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-outline-variant text-left text-xs uppercase tracking-wide text-on-surface-variant">
                <th className="px-2 py-2">Employee</th>
                <th className="px-2 py-2">Date</th>
                <th className="px-2 py-2">Requested in / out</th>
                <th className="px-2 py-2">Reason</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Raised</th>
                <th className="px-2 py-2 text-right">—</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item._id} className="border-b border-outline-variant/50">
                  <td className="px-2 py-1.5 text-on-surface">{item.user_id?.display_name ?? '—'}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{item.log_date}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    {item.requested_in || '—'} / {item.requested_out || '—'}
                  </td>
                  <td className="max-w-[240px] truncate px-2 py-1.5 text-on-surface-variant" title={item.reason}>
                    {item.reason}
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge tone={TONE[item.status] ?? 'neutral'}>{item.status}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-xs text-on-surface-variant">
                    {istDateTime(item.created_at)}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {item.status === 'pending' && (
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          title="Approve"
                          className="rounded p-1 text-success hover:bg-success/10"
                          onClick={() => decideOn(item._id, true)}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Reject"
                          className="rounded p-1 text-danger hover:bg-danger/10"
                          onClick={() => decideOn(item._id, false)}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="Raise a regularisation">
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface">Date</label>
            <Input
              type="date"
              value={form.log_date}
              onChange={e => setForm(f => ({ ...f, log_date: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface">In time</label>
              <Input
                type="time"
                value={form.requested_in}
                onChange={e => setForm(f => ({ ...f, requested_in: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface">Out time</label>
              <Input
                type="time"
                value={form.requested_out}
                onChange={e => setForm(f => ({ ...f, requested_out: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface">Reason</label>
            <Input
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="e.g. client visit, forgot to punch out"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={submit} disabled={raise.isPending}>Submit</Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
